import { createContext, useContext, useState, type ReactNode } from 'react';

export type DataPurpose = 'production' | 'demo' | 'store_review' | 'internal_test';
export type DataScope = DataPurpose | 'all';
export type DataPurposeEntity = 'users' | 'clubs' | 'teams';

const scopes: DataScope[] = ['production', 'demo', 'store_review', 'internal_test', 'all'];
const storageKey = 'evalyte.admin.dataScope';

interface ScopeContextValue {
  scope: DataScope;
  setScope: (scope: DataScope) => void;
}

const ScopeContext = createContext<ScopeContextValue | null>(null);

export function DataScopeProvider({ children }: { children: ReactNode }) {
  const [scope, updateScope] = useState<DataScope>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return scopes.find((candidate) => candidate === stored) ?? 'production';
    } catch {
      return 'production';
    }
  });

  function setScope(next: DataScope) {
    updateScope(next);
    try { sessionStorage.setItem(storageKey, next); } catch { /* Keep the current tab's choice. */ }
  }

  return <ScopeContext.Provider value={{ scope, setScope }}>{children}</ScopeContext.Provider>;
}

export function useDataScope(): ScopeContextValue {
  const context = useContext(ScopeContext);
  if (!context) throw new Error('DataScopeProvider is missing');
  return context;
}
